import { useMemo, useRef, useState, useEffect } from "react";
import { Check, ChevronDown, X, RotateCcw } from "lucide-react";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";

/**
 * UnifiedFilters — Single filter component used across CA Journalier, Dashboard,
 * KPI Achats, Hygiène and Rentabilité. Sourced exclusively from `corner_info`.
 *
 * Value shape:
 *   {
 *     regions, secteurs, corners, marques, enseignes, types, drs, bms,   ← structure
 *     year_mode: "civil" | "fiscal", annees, semaines, date_from, date_to  ← période
 *   }
 * Props:
 *   filters         — payload from GET /api/kpi/filters
 *   value           — current selection
 *   onChange        — (next) => void
 *   showDate        — show date-from / date-to (default true)
 *   showWeek        — show week multi-select (default true)
 *   showDR / showBM — optional (default false; structure row already busy)
 */

function Dropdown({ label, options, value = [], onChange, testid, minW = 130, className = "" }) {
  return <FilterMultiSelect label={label} options={options} value={value} onChange={onChange} testid={testid} minWidth={minW} className={className} size="xs" placeholder={label} menuWidth={220}/>;
}

const MONTHS = ["JANVIER","FEVRIER","MARS","AVRIL","MAI","JUIN","JUILLET","AOUT","SEPTEMBRE","OCTOBRE","NOVEMBRE","DECEMBRE"];

export default function UnifiedFilters({
  filters,
  value,
  onChange,
  showDate = true,
  showWeek = true,
  showDR = false,
  showBM = false,
  showMonth = false,
}) {
  const v = value || {};
  const regions   = v.regions || [];
  const secteurs  = v.secteurs || [];
  const corners   = v.corners || [];
  const marques   = v.marques || [];
  const enseignes = v.enseignes || [];
  const types     = v.types || [];
  const drs       = v.drs || [];
  const bms       = v.bms || [];
  const annees    = v.annees || [];
  const semaines  = v.semaines || [];
  const mois      = v.mois || [];
  const yearMode  = v.year_mode || "civil";

  // ─── Cascade options built from filters.corners[] ───────────
  const allCorners = filters?.corners || [];

  const secteurOptions = useMemo(() => {
    const set = new Set();
    allCorners.forEach(c => {
      if ((regions.length === 0 || regions.includes(c.region))
          && (marques.length === 0 || marques.includes(c.marque))
          && (enseignes.length === 0 || enseignes.includes(c.enseigne))
          && (types.length === 0 || types.includes(c.type))) {
        if (c.secteur) set.add(c.secteur);
      }
    });
    return [...set].sort();
  }, [allCorners, regions, marques, enseignes, types]);

  const cornerOptions = useMemo(() => {
    const filtered = allCorners
      .filter(c => regions.length === 0 || regions.includes(c.region))
      .filter(c => secteurs.length === 0 || secteurs.includes(c.secteur))
      .filter(c => marques.length === 0 || marques.includes(c.marque))
      .filter(c => enseignes.length === 0 || enseignes.includes(c.enseigne))
      .filter(c => types.length === 0 || types.includes(c.type))
      .filter(c => drs.length === 0 || drs.includes(c.dr))
      .filter(c => bms.length === 0 || bms.includes(c.bm));
    // Disambiguate duplicated "Corners" values (2 in prod: STESTEVE, LEBLANC)
    const cnt = filtered.reduce((m, c) => {
      const k = (c.corners_short || c.corner || "").trim();
      m[k] = (m[k] || 0) + 1; return m;
    }, {});
    return filtered
      .map(c => {
        const short = (c.corners_short || c.corner || "").trim();
        const label = cnt[short] > 1 ? `${short} · ${c.enseigne || c.corner}` : short;
        // Value = corners_key (short name, e.g. "CAPBRETON") — matches kpi_daily_ca.corner_key
        return { value: c.corners_key || c.corner_key, label };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allCorners, regions, secteurs, marques, enseignes, types, drs, bms]);

  const drOptions = useMemo(() => {
    const m = new Map();
    allCorners.forEach(c => {
      if ((regions.length === 0 || regions.includes(c.region))
          && (secteurs.length === 0 || secteurs.includes(c.secteur))
          && (marques.length === 0 || marques.includes(c.marque))
          && (enseignes.length === 0 || enseignes.includes(c.enseigne))) {
        if (c.dr) m.set(c.dr, c.dr_prenom || c.dr);
      }
    });
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1])).map(([value, label]) => ({ value, label }));
  }, [allCorners, regions, secteurs, marques, enseignes]);

  const bmOptions = useMemo(() => {
    const m = new Map();
    allCorners.forEach(c => {
      if ((regions.length === 0 || regions.includes(c.region))
          && (secteurs.length === 0 || secteurs.includes(c.secteur))
          && (marques.length === 0 || marques.includes(c.marque))
          && (enseignes.length === 0 || enseignes.includes(c.enseigne))
          && (types.length === 0 || types.includes(c.type))
          && (drs.length === 0 || drs.includes(c.dr))) {
        if (c.bm) m.set(c.bm, c.bm_prenom || c.bm);
      }
    });
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1])).map(([value, label]) => ({ value, label }));
  }, [allCorners, regions, secteurs, marques, enseignes, types, drs]);

  const yearOptions = (yearMode === "fiscal"
    ? (filters?.fiscal_years || filters?.years || [])
    : (filters?.years || [])
  ).map(y => ({ value: Number(y), label: yearMode === "fiscal" ? `FY${String(y).slice(-2)}` : String(y) }));

  const weekOptions = (filters?.weeks || []).map(w => ({ value: Number(w), label: `S${String(w).padStart(2, "0")}` }));

  const patch = (o) => onChange({ ...v, ...o });
  const reset = () => onChange({ year_mode: yearMode });

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-2 space-y-1.5" data-testid="unified-filters">
      {/* ── Row 1 · STRUCTURE ── */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 pr-1 hidden sm:inline">Structure</span>
        <Dropdown label="Région" testid="uf-regions" options={filters?.regions || []} value={regions}
                  onChange={x => patch({ regions: x, secteurs: [], drs: [], bms: [], corners: [] })} minW={110} className="flex-1 sm:flex-none"/>
        <Dropdown label="Secteur" testid="uf-secteurs" options={secteurOptions} value={secteurs}
                  onChange={x => patch({ secteurs: x, drs: [], bms: [], corners: [] })} minW={110} className="flex-1 sm:flex-none"/>
        <Dropdown label="Corners" testid="uf-corners" options={cornerOptions} value={corners}
                  onChange={x => patch({ corners: x })} minW={130} className="flex-1 sm:flex-none"/>
        <Dropdown label="Marque" testid="uf-marques" options={filters?.marques || []} value={marques}
                  onChange={x => patch({ marques: x, secteurs: [], corners: [] })} minW={100} className="flex-1 sm:flex-none"/>
        <Dropdown label="Enseigne" testid="uf-enseignes" options={filters?.enseignes || []} value={enseignes}
                  onChange={x => patch({ enseignes: x, secteurs: [], corners: [] })} minW={110} className="flex-1 sm:flex-none"/>
        <Dropdown label="Type" testid="uf-types" options={filters?.types || []} value={types}
                  onChange={x => patch({ types: x, corners: [] })} minW={90} className="flex-1 sm:flex-none"/>
        {showDR && (
          <Dropdown label="DR" testid="uf-drs" options={drOptions} value={drs}
                    onChange={x => patch({ drs: x, bms: [], corners: [] })} minW={100} className="flex-1 sm:flex-none"/>
        )}
        {showBM && (
          <Dropdown label="BM" testid="uf-bms" options={bmOptions} value={bms}
                    onChange={x => patch({ bms: x, corners: [] })} minW={100} className="flex-1 sm:flex-none"/>
        )}
      </div>

      {/* ── Row 2 · PÉRIODE ── */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 pr-1 hidden sm:inline">Période</span>
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-[10px] font-semibold">
          <button data-testid="uf-mode-civil" onClick={() => patch({ year_mode: "civil" })}
                  className={`px-2 py-1.5 ${yearMode === "civil" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Civile</button>
          <button data-testid="uf-mode-fiscal" onClick={() => patch({ year_mode: "fiscal" })}
                  className={`px-2 py-1.5 ${yearMode === "fiscal" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Exercice</button>
        </div>
        <Dropdown label={yearMode === "fiscal" ? "FY" : "Années"} testid="uf-years"
                  options={yearOptions} value={annees}
                  onChange={x => patch({ annees: x.map(Number) })} minW={90}/>
        {showDate && (
          <>
            <input data-testid="uf-from" type="date" value={v.date_from || ""}
                   onChange={e => patch({ date_from: e.target.value })}
                   className="px-2 py-1.5 rounded-md border border-slate-200 bg-white text-[11px]"/>
            <span className="text-[10px] text-slate-400 hidden sm:inline">→</span>
            <input data-testid="uf-to" type="date" value={v.date_to || ""}
                   onChange={e => patch({ date_to: e.target.value })}
                   className="px-2 py-1.5 rounded-md border border-slate-200 bg-white text-[11px]"/>
          </>
        )}
        {showWeek && (
          <Dropdown label="Semaine" testid="uf-weeks" options={weekOptions} value={semaines}
                    onChange={x => patch({ semaines: x.map(Number) })} minW={100}/>
        )}
        {showMonth && (
          <Dropdown label="Mois" testid="uf-months" options={MONTHS} value={mois}
                    onChange={x => patch({ mois: x })} minW={90}/>
        )}
        <button data-testid="uf-reset" onClick={reset}
                className="ml-auto flex items-center gap-1 px-2 py-1.5 rounded-md bg-white border border-slate-200 hover:bg-slate-50 text-[10px] font-semibold text-slate-600">
          <RotateCcw className="w-2.5 h-2.5"/> Réinitialiser
        </button>
      </div>
    </div>
  );
}
